import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Heading,
    VStack,
    HStack,
    Text,
    Card,
    CardBody,
    CardHeader,
    IconButton,
    Tooltip,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    useToast,
    Badge,
    Divider,
    Spinner,
    Alert,
    AlertIcon,
} from "@chakra-ui/react";
import { FiPlus, FiEdit2, FiTrash2, FiMapPin } from "react-icons/fi";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../../contexts/AuthContext";
import { getColor } from "../../brandConfig";
import { GET_CLIENT_SHIPPING, UPDATE_CLIENT_SHIPPING, ADD_CLIENT_SHIPPING_ADDRESS } from "./queries";

interface AddressFormData {
    name: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    instructions: string;
}

const emptyForm: AddressFormData = {
    name: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    postcode: "",
    country: "Australia",
    instructions: "",
};

const ShippingAddresses: React.FC = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const toast = useToast();
    const { user } = useAuth();

    // Consistent styling from brandConfig
    const bg = getColor("background.main");
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");

    const [formData, setFormData] = useState<AddressFormData>({ ...emptyForm });

    // Query shipping addresses from backend
    const { data, loading, error, refetch } = useQuery(GET_CLIENT_SHIPPING, {
        variables: { id: user?.id },
        skip: !user?.id,
    });

    const [updateShipping, { loading: updateLoading }] = useMutation(UPDATE_CLIENT_SHIPPING, {
        onCompleted: () => {
            refetch();
        },
        onError: (err) => {
            toast({
                title: "Error",
                description: err.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const [addShippingAddress, { loading: addLoading }] = useMutation(ADD_CLIENT_SHIPPING_ADDRESS, {
        onCompleted: () => {
            refetch();
        },
        onError: (err) => {
            toast({
                title: "Error",
                description: err.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        },
    });

    const addresses = data?.client?.shippingAddresses || [];

    const handleAddAddress = () => {
        setEditingIndex(null);
        setFormData({ ...emptyForm });
        onOpen();
    };

    const handleEditAddress = (index: number) => {
        const address = addresses[index];
        setEditingIndex(index);
        setFormData({
            name: address.name,
            phone: address.phone,
            street: address.address.street,
            city: address.address.city,
            state: address.address.state,
            postcode: address.address.postcode,
            country: address.address.country,
            instructions: address.instructions || "",
        });
        onOpen();
    };

    const stripTypename = (addr: any) => ({
        name: addr.name,
        phone: addr.phone,
        address: {
            street: addr.address.street,
            city: addr.address.city,
            state: addr.address.state,
            postcode: addr.address.postcode,
            country: addr.address.country,
        },
        isDefault: addr.isDefault,
        instructions: addr.instructions || undefined,
    });

    const handleDeleteAddress = async (index: number) => {
        const updated = addresses.filter((_: any, i: number) => i !== index).map(stripTypename);

        // If we deleted the default, make the first one default
        if (updated.length > 0 && !updated.some((a: any) => a.isDefault)) {
            updated[0].isDefault = true;
        }

        await updateShipping({
            variables: {
                id: user?.id,
                input: { shippingAddresses: updated },
            },
        });

        toast({
            title: "Address deleted",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    };

    const handleSetDefault = async (index: number) => {
        const updated = addresses.map((addr: any, i: number) => ({
            ...stripTypename(addr),
            isDefault: i === index,
        }));

        await updateShipping({
            variables: {
                id: user?.id,
                input: { shippingAddresses: updated },
            },
        });

        toast({
            title: "Default address updated",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
    };

    const handleSaveAddress = async () => {
        const addressInput = {
            name: formData.name,
            phone: formData.phone,
            address: {
                street: formData.street,
                city: formData.city,
                state: formData.state,
                postcode: formData.postcode,
                country: formData.country,
            },
            isDefault: addresses.length === 0, // First address is default
            instructions: formData.instructions || undefined,
        };

        if (editingIndex !== null) {
            // Update existing: replace in array and send full array
            const updated = addresses.map((addr: any, i: number) =>
                i === editingIndex
                    ? { ...stripTypename(addr), ...addressInput, isDefault: addr.isDefault }
                    : stripTypename(addr)
            );

            await updateShipping({
                variables: {
                    id: user?.id,
                    input: { shippingAddresses: updated },
                },
            });

            toast({
                title: "Address updated",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } else {
            // Add new: use dedicated mutation
            await addShippingAddress({
                variables: {
                    input: addressInput,
                },
            });

            toast({
                title: "Address added",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        }
        onClose();
    };

    const handleInputChange = (field: keyof AddressFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const isSaving = updateLoading || addLoading;

    if (loading) {
        return (
            <VStack spacing={8}>
                <Spinner size="xl" />
                <Text color={textSecondary}>Loading shipping addresses...</Text>
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Text>Error loading addresses: {error.message}</Text>
            </Alert>
        );
    }

    return (
        <>
            <VStack spacing={6} align="stretch">
                    <Box>
                        <VStack spacing={4} align="stretch" mb={4}>
                            <VStack align="start" spacing={2}>
                                <HStack spacing={3} align="center">
                                    <Heading size="lg" color={textPrimary}>
                                        Shipping Addresses
                                    </Heading>
                                    <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">CUSTOMER</Badge>
                                </HStack>
                                <Text color={textSecondary}>
                                    Manage your shipping addresses for product orders and deliveries.
                                </Text>
                            </VStack>

                            {/* Mobile-responsive button container */}
                            <Box display="flex" justifyContent={{ base: "stretch", md: "flex-end" }}>
                                <Button
                                    leftIcon={<FiPlus />}
                                    bg="white"
                                    color="black"
                                    _hover={{
                                        bg: "gray.100",
                                        transform: "translateY(-2px)"
                                    }}
                                    onClick={handleAddAddress}
                                    boxShadow="0 2px 4px rgba(255, 255, 255, 0.1)"
                                    width={{ base: "100%", md: "auto" }}
                                    minW={{ md: "140px" }}
                                    flexShrink={0}
                                    isDisabled={isSaving}
                                >
                                    Add Address
                                </Button>
                            </Box>
                        </VStack>
                    </Box>

                    <VStack spacing={4} align="stretch">
                        {addresses.length === 0 ? (
                            <Card
                                bg={cardGradientBg}
                                backdropFilter="blur(10px)"
                                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                                border="1px"
                                borderColor={cardBorder}
                            >
                                <CardBody textAlign="center" py={8}>
                                    <Box display="inline-block">
                                        <FiMapPin size={48} color={textMuted} />
                                    </Box>
                                    <Text mt={4} color={textMuted}>
                                        No shipping addresses yet
                                    </Text>
                                    <Button
                                        mt={4}
                                        leftIcon={<FiPlus />}
                                        bg="white"
                                        color="black"
                                        _hover={{
                                            bg: "gray.100",
                                            transform: "translateY(-2px)"
                                        }}
                                        onClick={handleAddAddress}
                                        width={{ base: "100%", sm: "auto" }}
                                        minW={{ sm: "200px" }}
                                    >
                                        Add Your First Address
                                    </Button>
                                </CardBody>
                            </Card>
                        ) : (
                            addresses.map((address: any, index: number) => (
                                <Card
                                    key={index}
                                    bg={cardGradientBg}
                                    backdropFilter="blur(10px)"
                                    boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                                    border="1px"
                                    borderColor={cardBorder}
                                >
                                    <CardHeader borderBottom="1px" borderColor={cardBorder}>
                                        <HStack justify="space-between">
                                            <HStack>
                                                <Text fontWeight="bold" color={textPrimary}>{address.name}</Text>
                                                {address.isDefault && (
                                                    <Badge
                                                        bg="rgba(34, 197, 94, 0.2)"
                                                        color="#22C55E"
                                                        border="1px solid"
                                                        borderColor="rgba(34, 197, 94, 0.3)"
                                                    >Default</Badge>
                                                )}
                                            </HStack>
                                            <HStack>
                                                <Tooltip label="Edit address" hasArrow openDelay={300}>
                                                    <IconButton
                                                        aria-label="Edit address"
                                                        icon={<FiEdit2 />}
                                                        size="sm"
                                                        variant="ghost"
                                                        color="#3B82F6"
                                                        _hover={{ bg: "rgba(59, 130, 246, 0.2)", color: "#3B82F6" }}
                                                        onClick={() => handleEditAddress(index)}
                                                        isDisabled={isSaving}
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    label={address.isDefault ? "Mark another address as default before deleting" : "Delete address"}
                                                    hasArrow
                                                    openDelay={300}
                                                >
                                                    <IconButton
                                                        aria-label="Delete address"
                                                        icon={<FiTrash2 />}
                                                        size="sm"
                                                        variant="ghost"
                                                        color="#EF4444"
                                                        _hover={{ bg: "rgba(239, 68, 68, 0.2)", color: "#EF4444" }}
                                                        onClick={() => handleDeleteAddress(index)}
                                                        isDisabled={address.isDefault || isSaving}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </HStack>
                                    </CardHeader>
                                    <CardBody pt={0}>
                                        <VStack align="start" spacing={1}>
                                            <Text color={textPrimary}>{address.address.street}</Text>
                                            <Text color={textSecondary}>
                                                {address.address.city}, {address.address.state} {address.address.postcode}
                                            </Text>
                                            <Text color={textSecondary}>{address.address.country}</Text>
                                            <Text color={textMuted} fontSize="sm">{address.phone}</Text>
                                            {address.instructions && (
                                                <Text color={textMuted} fontSize="sm" fontStyle="italic">
                                                    {address.instructions}
                                                </Text>
                                            )}
                                        </VStack>
                                        {!address.isDefault && (
                                            <>
                                                <Divider my={3} borderColor={cardBorder} />
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor={cardBorder}
                                                    color={textPrimary}
                                                    _hover={{
                                                        borderColor: textSecondary,
                                                        bg: "rgba(255, 255, 255, 0.05)"
                                                    }}
                                                    onClick={() => handleSetDefault(index)}
                                                    isDisabled={isSaving}
                                                >
                                                    Set as Default
                                                </Button>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            ))
                        )}
                    </VStack>
            </VStack>

            {/* Add/Edit Address Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay bg="rgba(0, 0, 0, 0.8)" />
                <ModalContent
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    border="1px"
                    borderColor={cardBorder}
                >
                    <ModalHeader color={textPrimary} borderBottom="1px" borderColor={cardBorder}>
                        {editingIndex !== null ? "Edit Address" : "Add New Address"}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel color={textPrimary}>Address Name</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                    placeholder="e.g., Home, Work, Office"
                                    bg="rgba(0, 0, 0, 0.2)"
                                    border="1px"
                                    borderColor={cardBorder}
                                    color={textPrimary}
                                    _placeholder={{ color: textMuted }}
                                    _focus={{
                                        borderColor: textSecondary,
                                        boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                    }}
                                    _hover={{ borderColor: textSecondary }}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color={textPrimary}>Phone</FormLabel>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange("phone", e.target.value)}
                                    placeholder="+61412345678"
                                    bg="rgba(0, 0, 0, 0.2)"
                                    border="1px"
                                    borderColor={cardBorder}
                                    color={textPrimary}
                                    _placeholder={{ color: textMuted }}
                                    _focus={{
                                        borderColor: textSecondary,
                                        boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                    }}
                                    _hover={{ borderColor: textSecondary }}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color={textPrimary}>Street Address</FormLabel>
                                <Input
                                    value={formData.street}
                                    onChange={(e) => handleInputChange("street", e.target.value)}
                                    placeholder="123 Main Street"
                                    bg="rgba(0, 0, 0, 0.2)"
                                    border="1px"
                                    borderColor={cardBorder}
                                    color={textPrimary}
                                    _placeholder={{ color: textMuted }}
                                    _focus={{
                                        borderColor: textSecondary,
                                        boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                    }}
                                    _hover={{ borderColor: textSecondary }}
                                />
                            </FormControl>

                            <HStack width="100%">
                                <FormControl isRequired>
                                    <FormLabel color={textPrimary}>City</FormLabel>
                                    <Input
                                        value={formData.city}
                                        onChange={(e) => handleInputChange("city", e.target.value)}
                                        bg="rgba(0, 0, 0, 0.2)"
                                        border="1px"
                                        borderColor={cardBorder}
                                        color={textPrimary}
                                        _placeholder={{ color: textMuted }}
                                        _focus={{
                                            borderColor: textSecondary,
                                            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                        }}
                                        _hover={{ borderColor: textSecondary }}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel color={textPrimary}>State</FormLabel>
                                    <Select
                                        value={formData.state}
                                        onChange={(e) => handleInputChange("state", e.target.value)}
                                        bg="rgba(0, 0, 0, 0.2)"
                                        border="1px"
                                        borderColor={cardBorder}
                                        color={textPrimary}
                                        _hover={{ borderColor: textSecondary }}
                                        _focus={{
                                            borderColor: textSecondary,
                                            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                        }}
                                    >
                                        <option value="" style={{ backgroundColor: '#1a1a1a' }}>Select State</option>
                                        <option value="NSW" style={{ backgroundColor: '#1a1a1a' }}>NSW</option>
                                        <option value="VIC" style={{ backgroundColor: '#1a1a1a' }}>VIC</option>
                                        <option value="QLD" style={{ backgroundColor: '#1a1a1a' }}>QLD</option>
                                        <option value="WA" style={{ backgroundColor: '#1a1a1a' }}>WA</option>
                                        <option value="SA" style={{ backgroundColor: '#1a1a1a' }}>SA</option>
                                        <option value="TAS" style={{ backgroundColor: '#1a1a1a' }}>TAS</option>
                                        <option value="ACT" style={{ backgroundColor: '#1a1a1a' }}>ACT</option>
                                        <option value="NT" style={{ backgroundColor: '#1a1a1a' }}>NT</option>
                                    </Select>
                                </FormControl>
                            </HStack>

                            <HStack width="100%">
                                <FormControl isRequired>
                                    <FormLabel color={textPrimary}>Postal Code</FormLabel>
                                    <Input
                                        value={formData.postcode}
                                        onChange={(e) => handleInputChange("postcode", e.target.value)}
                                        bg="rgba(0, 0, 0, 0.2)"
                                        border="1px"
                                        borderColor={cardBorder}
                                        color={textPrimary}
                                        _placeholder={{ color: textMuted }}
                                        _focus={{
                                            borderColor: textSecondary,
                                            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                        }}
                                        _hover={{ borderColor: textSecondary }}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel color={textPrimary}>Country</FormLabel>
                                    <Select
                                        value={formData.country}
                                        onChange={(e) => handleInputChange("country", e.target.value)}
                                        bg="rgba(0, 0, 0, 0.2)"
                                        border="1px"
                                        borderColor={cardBorder}
                                        color={textPrimary}
                                        _hover={{ borderColor: textSecondary }}
                                        _focus={{
                                            borderColor: textSecondary,
                                            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                        }}
                                    >
                                        <option value="Australia" style={{ backgroundColor: '#1a1a1a' }}>Australia</option>
                                        <option value="New Zealand" style={{ backgroundColor: '#1a1a1a' }}>New Zealand</option>
                                        <option value="United States" style={{ backgroundColor: '#1a1a1a' }}>United States</option>
                                        <option value="Canada" style={{ backgroundColor: '#1a1a1a' }}>Canada</option>
                                        <option value="United Kingdom" style={{ backgroundColor: '#1a1a1a' }}>United Kingdom</option>
                                    </Select>
                                </FormControl>
                            </HStack>

                            <FormControl>
                                <FormLabel color={textPrimary}>Delivery Instructions</FormLabel>
                                <Input
                                    value={formData.instructions}
                                    onChange={(e) => handleInputChange("instructions", e.target.value)}
                                    placeholder="e.g., Leave at front door"
                                    bg="rgba(0, 0, 0, 0.2)"
                                    border="1px"
                                    borderColor={cardBorder}
                                    color={textPrimary}
                                    _placeholder={{ color: textMuted }}
                                    _focus={{
                                        borderColor: textSecondary,
                                        boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                    }}
                                    _hover={{ borderColor: textSecondary }}
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px" borderColor={cardBorder}>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={onClose}
                            color={textPrimary}
                            _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                            isDisabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            bg="white"
                            color="black"
                            _hover={{
                                bg: "gray.100",
                                transform: "translateY(-2px)"
                            }}
                            onClick={handleSaveAddress}
                            isLoading={isSaving}
                            loadingText="Saving..."
                            isDisabled={!formData.name || !formData.phone || !formData.street || !formData.city || !formData.state || !formData.postcode}
                        >
                            {editingIndex !== null ? "Update" : "Add"} Address
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </>
    );
};

export default ShippingAddresses;
